/**
 * The Rendering Engine - Block Registry
 * Maps Admin block types to React components
 */

import React, { lazy } from 'react';
import type { RenderableContent } from '@agentic/core-ui/types/editor';
import { sanitizeCmsHtml } from '@agentic/magento-client/lib/cms-sanitize';
// Extension seam (additive). getExtensionBlock is consulted ONLY on the existing
// static-miss path of getBlockComponent below, so registered blocks resolve while
// every built-in block is unaffected. The side-effect import runs any compiled-in
// registrations before the first render (empty in Phase 0).
import { getExtensionBlock } from '../../extensions/registry';
import '../../extensions/registerExtensions';

// A. STRUCTURAL BLOCKS (6)
import { Container } from '../blocks/structural/Container';
import { GridRow } from '../blocks/structural/GridRow';
import { Column } from '../blocks/structural/Column';
import { Tabs } from '../blocks/structural/Tabs';
import { Accordion } from '../blocks/structural/Accordion';

// B. GLOBAL BLOCKS (7)
import { Logo } from '../blocks/global/Logo';
import { MegaMenu } from '../blocks/global/MegaMenu';
import { NavMenuButton } from '../blocks/global/NavMenuButton';
// Desktop search owns autocomplete and animation code. Mobile pages and
// unrelated block trees should not download it until they render this block.
const SearchBar = import.meta.env.SSR ? (await import('../blocks/global/SearchBar')).SearchBar : lazy(() => import('../blocks/global/SearchBar').then(m => ({ default: m.SearchBar })));
import { MiniCart } from '../blocks/global/MiniCart';
import { MiniQuoteCart } from '../blocks/global/MiniQuoteCart';
import { UserLinks } from '../blocks/global/UserLinks';
import { StoreSwitcher } from '../blocks/global/StoreSwitcher';
import { NewsletterSubscribe } from '../blocks/global/NewsletterSubscribe';
import { FooterLinks } from '../blocks/global/FooterLinks';
import { Navigation } from '../blocks/global/Navigation';
import { SocialLinks } from '../blocks/global/SocialLinks';
import { PaymentIcons } from '../blocks/global/PaymentIcons';
import { Copyright } from '../blocks/global/Copyright';
import { WishlistIcon } from '../blocks/global/WishlistIcon';
import { LanguageSwitcher } from '../blocks/global/LanguageSwitcher';
import { ThemeTogglePill } from '../blocks/global/ThemeTogglePill';
import { VatToggle } from '../blocks/global/VatToggle';
import { CurrencySelector } from '../blocks/global/CurrencySelector';
import { HelpPopover } from '../blocks/global/HelpPopover';
import { FinderTrigger } from '../blocks/global/FinderTrigger';
import { HeaderTrustBar } from '../blocks/global/HeaderTrustBar';
import { TrustpilotWidget } from '../blocks/global/TrustpilotWidget';
import { ContactPhone } from '../blocks/global/ContactPhone';

// C. PDP BLOCKS — lazy-loaded (only on PDP, not on homepage)
// Universal commerce blocks kept eager (used on homepage grids):
import { ProductCard } from '../blocks/commerce/ProductCard';
// ProductGallery moved to EAGER imports.
// Why: this block contains the LCP <img> on every PDP. As a lazy()
// import it landed in the commerce-pdp chunk (~36KB, parses ~250ms
// on 4x-throttled mobile), and Suspense temporarily unmounted the
// gallery during client hydration — pushing LCP from ~500ms to
// ~3,300ms on mobile. Eager import bundles ProductGallery into the
// BlockRenderer chunk (loads ~400ms earlier) and removes the
// Suspense replay path entirely.
import { ProductGallery } from '../blocks/commerce/ProductGallery';
// CategoryCards demoted to lazy. Used on
// homepage + category pages only; previously eager-imported into the
// BlockRenderer chunk, costing ~2.7 KiB unused on every PDP visit
// (Lighthouse "Reduce unused JavaScript" specifically named this file).
//
// SSR-eager / client-lazy split on every lazy block below.
// Why: lazy() SUSPENDS during streaming SSR, so React emitted Suspense
// boundary artifacts into every rendered page — skeleton fallbacks in
// place, the real block HTML in hidden `S:n` segment divs at the end of
// the island, plus $RC completion scripts that relocate them at runtime.
// Inside an astro-island those leftovers sit inside the hydration root,
// so hydrateRoot() saw nodes it never renders → React #418 on every
// page, and crawlers without JS saw skeletons instead of content.
// `import.meta.env.SSR` is statically replaced at build time: the server
// bundle gets plain eager imports (content rendered in place, no
// boundary artifacts); the client bundle keeps the lazy() code-split
// chunks. Hydration of a still-loading lazy block SUSPENDS and keeps the
// server HTML — the designed React behaviour — so the two sides agree.
const CategoryCards = import.meta.env.SSR ? (await import('../blocks/commerce/CategoryCards')).CategoryCards : lazy(() => import('../blocks/commerce/CategoryCards').then(m => ({ default: m.CategoryCards })));

// PDP-only — converted to React.lazy. BlockRenderer wraps each in Suspense
// already, so these only download when their block type appears in a layout.
const ProductTitle = import.meta.env.SSR ? (await import('../blocks/commerce/ProductTitle')).ProductTitle : lazy(() => import('../blocks/commerce/ProductTitle').then(m => ({ default: m.ProductTitle })));
const ProductPrice = import.meta.env.SSR ? (await import('../blocks/commerce/ProductPrice')).ProductPrice : lazy(() => import('../blocks/commerce/ProductPrice').then(m => ({ default: m.ProductPrice })));
const ProductDescription = import.meta.env.SSR ? (await import('../blocks/commerce/ProductDescription')).ProductDescription : lazy(() => import('../blocks/commerce/ProductDescription').then(m => ({ default: m.ProductDescription })));
const ProductSales = import.meta.env.SSR ? (await import('../blocks/commerce/ProductSales')).ProductSales : lazy(() => import('../blocks/commerce/ProductSales').then(m => ({ default: m.ProductSales })));
const ShortDescription = import.meta.env.SSR ? (await import('../blocks/commerce/ShortDescription')).ShortDescription : lazy(() => import('../blocks/commerce/ShortDescription').then(m => ({ default: m.ShortDescription })));
const ProductAttribute = import.meta.env.SSR ? (await import('../blocks/commerce/ProductAttribute')).ProductAttribute : lazy(() => import('../blocks/commerce/ProductAttribute').then(m => ({ default: m.ProductAttribute })));
const ProductOptionsRouter = import.meta.env.SSR ? (await import('../blocks/commerce/ProductOptionsRouter')).ProductOptionsRouter : lazy(() => import('../blocks/commerce/ProductOptionsRouter').then(m => ({ default: m.ProductOptionsRouter })));
const QtySelector = import.meta.env.SSR ? (await import('../blocks/commerce/QtySelector')).QtySelector : lazy(() => import('../blocks/commerce/QtySelector').then(m => ({ default: m.QtySelector })));
const AddToCart = import.meta.env.SSR ? (await import('../blocks/commerce/AddToCart')).AddToCart : lazy(() => import('../blocks/commerce/AddToCart').then(m => ({ default: m.AddToCart })));
const WishlistBtn = import.meta.env.SSR ? (await import('../blocks/commerce/WishlistBtn')).WishlistBtn : lazy(() => import('../blocks/commerce/WishlistBtn').then(m => ({ default: m.WishlistBtn })));
const ProductTabs = import.meta.env.SSR ? (await import('../blocks/commerce/ProductTabs')).ProductTabs : lazy(() => import('../blocks/commerce/ProductTabs').then(m => ({ default: m.ProductTabs })));
const ProductFeatures = import.meta.env.SSR ? (await import('../blocks/commerce/ProductFeatures')).ProductFeatures : lazy(() => import('../blocks/commerce/ProductFeatures').then(m => ({ default: m.ProductFeatures })));
const Breadcrumbs = import.meta.env.SSR ? (await import('../blocks/commerce/Breadcrumbs')).Breadcrumbs : lazy(() => import('../blocks/commerce/Breadcrumbs').then(m => ({ default: m.Breadcrumbs })));
const ProductMeta = import.meta.env.SSR ? (await import('../blocks/commerce/ProductMeta')).ProductMeta : lazy(() => import('../blocks/commerce/ProductMeta').then(m => ({ default: m.ProductMeta })));
// Removed 2026-05-18: TrustBar (use HeaderTrustBar instead)
const CompareButton = import.meta.env.SSR ? (await import('../blocks/commerce/CompareButton')).CompareButton : lazy(() => import('../blocks/commerce/CompareButton').then(m => ({ default: m.CompareButton })));
const StockIndicator = import.meta.env.SSR ? (await import('../blocks/commerce/StockIndicator')).StockIndicator : lazy(() => import('../blocks/commerce/StockIndicator').then(m => ({ default: m.StockIndicator })));
// Removed 2026-05-18: Swatches, Price, Gallery, ProductBadge,
// ProductDetailsBlock, ProductOptionsBlock (canonical alternatives in use).
const ProductDocs = import.meta.env.SSR ? (await import('../blocks/commerce/ProductDocs')).ProductDocs : lazy(() => import('../blocks/commerce/ProductDocs').then(m => ({ default: m.ProductDocs })));
const WhatsInTheBox = import.meta.env.SSR ? (await import('../blocks/commerce/WhatsInTheBox')).WhatsInTheBox : lazy(() => import('../blocks/commerce/WhatsInTheBox').then(m => ({ default: m.WhatsInTheBox })));
const ProductAlertsBlock = import.meta.env.SSR ? (await import('../blocks/commerce/ProductAlertsBlock')).ProductAlertsBlock : lazy(() => import('../blocks/commerce/ProductAlertsBlock').then(m => ({ default: m.ProductAlertsBlock })));
const TierPricingBlock = import.meta.env.SSR ? (await import('../blocks/commerce/TierPricingBlock')).TierPricingBlock : lazy(() => import('../blocks/commerce/TierPricingBlock').then(m => ({ default: m.TierPricingBlock })));
const ReviewSummary = import.meta.env.SSR ? (await import('../blocks/commerce/ReviewSummary')).ReviewSummary : lazy(() => import('../blocks/commerce/ReviewSummary').then(m => ({ default: m.ReviewSummary })));

// Lazy-loaded (below-fold / heavy)
const UpsellGrid = import.meta.env.SSR ? (await import('../blocks/commerce/UpsellGrid')).UpsellGrid : lazy(() => import('../blocks/commerce/UpsellGrid').then(m => ({ default: m.UpsellGrid })));
const RelatedGrid = import.meta.env.SSR ? (await import('../blocks/commerce/RelatedGrid')).RelatedGrid : lazy(() => import('../blocks/commerce/RelatedGrid').then(m => ({ default: m.RelatedGrid })));
// Removed 2026-05-18: CrossSellGrid (upsell_grid covers the use case).
const QuickView = import.meta.env.SSR ? (await import('../blocks/commerce/QuickView')).QuickView : lazy(() => import('../blocks/commerce/QuickView').then(m => ({ default: m.QuickView })));
// Removed 2026-05-18: ProductList (product_grid_plp covers the use case).
const RelatedSidebar = import.meta.env.SSR ? (await import('../blocks/commerce/RelatedSidebar')).RelatedSidebar : lazy(() => import('../blocks/commerce/RelatedSidebar').then(m => ({ default: m.RelatedSidebar })));
const RecentlyViewed = import.meta.env.SSR ? (await import('../blocks/commerce/RecentlyViewed')).RecentlyViewed : lazy(() => import('../blocks/commerce/RecentlyViewed').then(m => ({ default: m.RecentlyViewed })));
// Removed 2026-05-18: Newsletter (newsletter_subscribe covers the use case).

// D. PLP BLOCKS — lazy-loaded (only on category pages)
const CategoryTitle = import.meta.env.SSR ? (await import('../blocks/commerce/CategoryTitle')).CategoryTitle : lazy(() => import('../blocks/commerce/CategoryTitle').then(m => ({ default: m.CategoryTitle })));
const CategoryDescription = import.meta.env.SSR ? (await import('../blocks/commerce/CategoryDescription')).CategoryDescription : lazy(() => import('../blocks/commerce/CategoryDescription').then(m => ({ default: m.CategoryDescription })));
const LayeredNavigation = import.meta.env.SSR ? (await import('../blocks/commerce/LayeredNavigation')).LayeredNavigation : lazy(() => import('../blocks/commerce/LayeredNavigation').then(m => ({ default: m.LayeredNavigation })));
const Toolbar = import.meta.env.SSR ? (await import('../blocks/commerce/Toolbar')).Toolbar : lazy(() => import('../blocks/commerce/Toolbar').then(m => ({ default: m.Toolbar })));
const ProductGridPlp = import.meta.env.SSR ? (await import('../blocks/commerce/ProductGridPlp')).ProductGridPlp : lazy(() => import('../blocks/commerce/ProductGridPlp').then(m => ({ default: m.ProductGridPlp })));
const Pagination = import.meta.env.SSR ? (await import('../blocks/commerce/Pagination')).Pagination : lazy(() => import('../blocks/commerce/Pagination').then(m => ({ default: m.Pagination })));

// E. CMS BLOCKS (10) + Text Block
// Universal content blocks — used on every page-type, kept eager.
import { RichText } from '../blocks/content/RichText';
import { Image } from '../blocks/content/Image';
import { Button } from '../blocks/content/Button';
import { IconList } from '../blocks/content/IconList';
import { Spacer } from '../blocks/structural/Spacer';
import { Divider } from '../blocks/structural/Divider';

// Page-specific CMS / hero blocks demoted to
// lazy. Used on homepage / category / CMS pages only. Previously eager-
// imported into BlockRenderer, costing ~10 KiB combined unused on every
// PDP visit. lazy() keeps Suspense boundary so once a layout JSON
// references one of these blocks, BlockRenderer's existing Suspense
// wrapper handles the load. No behavioural change for pages that DO
// use them — just a chunk reassignment.
const HeroBanner = import.meta.env.SSR ? (await import('../blocks/content/HeroBanner')).HeroBanner : lazy(() => import('../blocks/content/HeroBanner').then(m => ({ default: m.HeroBanner })));
// Removed 2026-05-18: HeroHeaderCenter, HeroHeaderLeft, SubTextSplit,
// HeadlineMassive, CategoryHook (demo/experimental CMS variants;
// hero_banner with variants covers the equivalent layouts).

// Lazy-loaded CMS content blocks (heavy / below-fold)
const Video = import.meta.env.SSR ? (await import('../blocks/content/Video')).Video : lazy(() => import('../blocks/content/Video').then(m => ({ default: m.Video })));
const BlogWidget = import.meta.env.SSR ? (await import('../blocks/content/BlogWidget')).BlogWidget : lazy(() => import('../blocks/content/BlogWidget').then(m => ({ default: m.BlogWidget })));
const HeroBannerImage = import.meta.env.SSR ? (await import('./../blocks/content/HeroBannerImage')).HeroBannerImage : lazy(() => import('./../blocks/content/HeroBannerImage').then(m => ({ default: m.HeroBannerImage })));
const TrustMetrics = import.meta.env.SSR ? (await import('./../blocks/content/TrustMetrics')).TrustMetrics : lazy(() => import('./../blocks/content/TrustMetrics').then(m => ({ default: m.TrustMetrics })));
const CallExperts = import.meta.env.SSR ? (await import('../blocks/content/CallExperts')).CallExperts : lazy(() => import('../blocks/content/CallExperts').then(m => ({ default: m.CallExperts })));
// Removed 2026-05-18: NewsletterSignup (newsletter_subscribe covers it).

// Text Block — renders preset text blocks ({text, tag, alignment}) as proper HTML elements
const TextBlock: BlockComponent = ({ data, style }) => {
  const tag = data?.tag || 'p';
  const inlineStyle: React.CSSProperties = { ...(style as React.CSSProperties) };
  if (typeof data?.color === 'string') inlineStyle.color = data.color;
  if (typeof data?.fontFamily === 'string') inlineStyle.fontFamily = data.fontFamily;
  if (typeof data?.fontWeight === 'string' || typeof data?.fontWeight === 'number') inlineStyle.fontWeight = data.fontWeight;
  if (typeof data?.textAlign === 'string') inlineStyle.textAlign = data.textAlign as React.CSSProperties['textAlign'];
  const ls = data?.letterSpacing;
  if (ls != null && (typeof ls === 'string' || typeof ls === 'number')) inlineStyle.letterSpacing = `${ls}px`;
  if (data?.textTransform) inlineStyle.textTransform = data.textTransform as React.CSSProperties['textTransform'];
  if (data?.fontSize) {
    const fs = typeof data.fontSize === 'object' ? (data.fontSize as { d?: string | number }).d : data.fontSize;
    if (fs != null && (typeof fs === 'string' || typeof fs === 'number')) inlineStyle.fontSize = `${fs}px`;
  }
  // Support raw HTML (e.g. anchor links, inline markup)
  if (data?.html) {
    return React.createElement(tag, {
      style: inlineStyle,
      // sanitizeCmsHtml strips XSS vectors — generic raw-HTML block from
      // Magento admin should not be able to ship <script>/inline handlers.
      dangerouslySetInnerHTML: { __html: sanitizeCmsHtml(data.html) },
    });
  }
  return React.createElement(tag, { style: inlineStyle }, data?.text ?? '');
};
const ContactForm = import.meta.env.SSR ? (await import('../blocks/content/ContactForm')).ContactForm : lazy(() => import('../blocks/content/ContactForm').then(m => ({ default: m.ContactForm })));
// Removed 2026-05-18: BentoGrid (niche aesthetic; product_grid + variants covers).
const BlogPostContent = import.meta.env.SSR ? (await import('../blocks/content/BlogPostContent')).BlogPostContent : lazy(() => import('../blocks/content/BlogPostContent').then(m => ({ default: m.BlogPostContent })));

// Lazy-loaded Content / Marketing blocks
const CountdownTimer = import.meta.env.SSR ? (await import('../blocks/content/CountdownTimer')).CountdownTimer : lazy(() => import('../blocks/content/CountdownTimer').then(m => ({ default: m.CountdownTimer })));
const TestimonialCard = import.meta.env.SSR ? (await import('../blocks/content/TestimonialCard')).TestimonialCard : lazy(() => import('../blocks/content/TestimonialCard').then(m => ({ default: m.TestimonialCard })));
// Removed 2026-05-18: PricingTable (use tier_pricing), StatsCounter,
// CarouselSlider, BeforeAfterSlider, SocialProofBar (niche/experimental).
const AlertBanner = import.meta.env.SSR ? (await import('../blocks/content/AlertBanner')).AlertBanner : lazy(() => import('../blocks/content/AlertBanner').then(m => ({ default: m.AlertBanner })));
const ComparisonTable = import.meta.env.SSR ? (await import('../blocks/content/ComparisonTable')).ComparisonTable : lazy(() => import('../blocks/content/ComparisonTable').then(m => ({ default: m.ComparisonTable })));
const ProductAssurance = import.meta.env.SSR ? (await import('../blocks/content/ProductAssurance')).ProductAssurance : lazy(() => import('../blocks/content/ProductAssurance').then(m => ({ default: m.ProductAssurance })));
const VoxfunlAudio = import.meta.env.SSR ? (await import('../blocks/content/VoxfunlAudio')).VoxfunlAudio : lazy(() => import('../blocks/content/VoxfunlAudio').then(m => ({ default: m.VoxfunlAudio })));

// F. CHECKOUT/CART BLOCKS (5)
const LoginForm = import.meta.env.SSR ? (await import('../blocks/commerce/LoginForm')).LoginForm : lazy(() => import('../blocks/commerce/LoginForm').then(m => ({ default: m.LoginForm })));

// G. ACCOUNT BLOCKS
const AccountNavigation = import.meta.env.SSR ? (await import('../blocks/commerce/AccountNavigation')).AccountNavigation : lazy(() => import('../blocks/commerce/AccountNavigation').then(m => ({ default: m.AccountNavigation })));
const AccountDashboard = import.meta.env.SSR ? (await import('../blocks/commerce/AccountDashboard')).AccountDashboard : lazy(() => import('../blocks/commerce/AccountDashboard').then(m => ({ default: m.AccountDashboard })));

// Blog landing grid — different shape from BlogWidget (sidebar/related strip).
const BlogGrid = import.meta.env.SSR ? (await import('../blocks/content/BlogGrid')).BlogGrid : lazy(() => import('../blocks/content/BlogGrid').then(m => ({ default: m.BlogGrid })));
const CartTable = import.meta.env.SSR ? (await import('../blocks/commerce/CartTable')).CartTable : lazy(() => import('../blocks/commerce/CartTable').then(m => ({ default: m.CartTable })));
const CartTotals = import.meta.env.SSR ? (await import('../blocks/commerce/CartTotals')).CartTotals : lazy(() => import('../blocks/commerce/CartTotals').then(m => ({ default: m.CartTotals })));
const CouponCode = import.meta.env.SSR ? (await import('../blocks/commerce/CouponCode')).CouponCode : lazy(() => import('../blocks/commerce/CouponCode').then(m => ({ default: m.CouponCode })));
const ShippingEstimator = import.meta.env.SSR ? (await import('../blocks/commerce/ShippingEstimator')).ShippingEstimator : lazy(() => import('../blocks/commerce/ShippingEstimator').then(m => ({ default: m.ShippingEstimator })));
const CheckoutSteps = import.meta.env.SSR ? (await import('../blocks/commerce/CheckoutSteps')).CheckoutSteps : lazy(() => import('../blocks/commerce/CheckoutSteps').then(m => ({ default: m.CheckoutSteps })));

// BLOCK COMPONENT TYPE
export interface BlockContextShape {
  storefrontHost?: string;
  product?: Record<string, unknown>;
  storeConfig?: Record<string, unknown>;
  siteContent?: Record<string, unknown>;
  cmsBlocks?: Array<{ identifier?: string; content?: string; title?: string }>;
  cmsPages?: Array<Record<string, unknown>>;
  categoryTree?: Record<string, unknown>;
  category?: Record<string, unknown>;
  breadcrumbs?: Array<{ name?: string; url?: string; category_id?: number | string }>;
  [key: string]: unknown;
}

export interface BlockComponentProps {
  data: RenderableContent | undefined;
  styles?: string; // Legacy raw styles
  className?: string; // Parsed tailwind classes
  style?: Record<string, string>; // Parsed CSS scoped variables
  context?: BlockContextShape;
  children?: React.ReactNode;
  variant?: string; // : visual variant (e.g. 'bento-dark', 'glass', 'minimal')
}

export type BlockComponent = React.FC<BlockComponentProps>;

// BLOCK MAP - Maps Admin type strings to React components
// Heterogeneous registry — each component declares its own narrow `data`
// shape (e.g. TabsContent for Tabs, LogoData for Logo). TypeScript's
// function-parameter contravariance prevents direct assignment to a single
// FC<BlockComponentProps>. Runtime safety is upheld by BlockRenderer
// resolving the correct component per block type and passing the data it
// already validated.
export const BLOCK_MAP = ({
  // A. STRUCTURAL BLOCKS (6) + layout JSON aliases
  container: Container,
  grid_row: GridRow,
  row: GridRow,        // alias — used in flat layout JSON (product.json, etc.)
  col: Column,         // alias — used in flat layout JSON
  column: Column,
  tabs: Tabs,
  accordion: Accordion,

  // B. GLOBAL BLOCKS — 2026-05-18 cleanup: pure aliases dropped (footer_navigation,
  // nav, quote_cart, header_why_choose_us, trustpilot, header_phone). Canonical
  // names below cover all in-use cases.
  logo: Logo,
  mega_menu: MegaMenu,
  nav_menu_button: NavMenuButton,
  search_bar: SearchBar,
  mini_cart: MiniCart,
  user_links: UserLinks,
  store_switcher: StoreSwitcher,
  newsletter_subscribe: NewsletterSubscribe,
  footer_links: FooterLinks,
  navigation: Navigation,
  social_links: SocialLinks,
  payment_icons: PaymentIcons,
  copyright: Copyright,
  wishlist_icon: WishlistIcon,
  language_switcher: LanguageSwitcher,
  theme_toggle: ThemeTogglePill,
  mini_quote_cart: MiniQuoteCart,
  vat_toggle: VatToggle,
  currency_selector: CurrencySelector,
  help_popover: HelpPopover,
  finder_trigger: FinderTrigger,
  header_trust_bar: HeaderTrustBar,
  trustpilot_widget: TrustpilotWidget,
  contact_phone: ContactPhone,

  // C. PDP BLOCKS — 2026-05-18 cleanup: dropped aliases (product_name, product_images,
  // cross_sell_grid, key_features, product_documents, package_contents, stock_alerts,
  // bulk_pricing, rating-summary) and unused experiments (crosssell_grid, trust_bar,
  // swatches, price, gallery, product_badge, product_list, product_details, product_options,
  // newsletter — newsletter_subscribe covers it).
  product_title: ProductTitle,
  product_price: ProductPrice,
  product_gallery: ProductGallery,
  product_description: ProductDescription,
  product_sales: ProductSales,
  short_description: ShortDescription,
  product_attribute: ProductAttribute,
  configurable_options: ProductOptionsRouter,
  qty_selector: QtySelector,
  add_to_cart: AddToCart,
  wishlist_btn: WishlistBtn,
  product_tabs: ProductTabs,
  upsell_grid: UpsellGrid,
  related_grid: RelatedGrid,
  product_features: ProductFeatures,
  breadcrumbs: Breadcrumbs,
  product_meta: ProductMeta,
  compare_button: CompareButton,
  quick_view: QuickView,
  stock_indicator: StockIndicator,
  product_card: ProductCard,
  product_docs: ProductDocs,
  whats_in_the_box: WhatsInTheBox,
  related_sidebar: RelatedSidebar,
  recently_viewed: RecentlyViewed,
  category_cards: CategoryCards,
  category_grid: CategoryCards,             // alias used in homepage presets — keep
  product_alerts: ProductAlertsBlock,
  tier_pricing: TierPricingBlock,
  review_summary: ReviewSummary,

  // D. PLP BLOCKS (6)
  category_title: CategoryTitle,
  category_description: CategoryDescription,
  layered_navigation: LayeredNavigation,
  toolbar: Toolbar,
  product_grid_plp: ProductGridPlp,
  product_grid_featured: ProductGridPlp,  // alias — used in homepage presets
  pagination: Pagination,

  // E. CMS BLOCKS — 2026-05-18 cleanup: dropped pure aliases (heading, paragraph,
  // feature_grid, why_choose_us, call_the_experts) and demo/experiment blocks
  // (hero_header_center/left, subtext_split, headline_massive, category_hook,
  // bento_grid, newsletter_signup — newsletter_subscribe covers it).
  hero_banner: HeroBanner,
  hero_banner_image: HeroBannerImage,
  cms_block: RichText,         // alias used widely in presets — keep
  rich_text: RichText,
  text: TextBlock,
  image: Image,
  video: Video,
  button: Button,
  icon_list: IconList,
  spacer: Spacer,
  divider: Divider,
  blog_widget: BlogWidget,
  contact_form: ContactForm,
  trust_metrics: TrustMetrics,
  call_experts: CallExperts,

  // G. CONTENT / MARKETING BLOCKS — 2026-05-18 cleanup: dropped niche/demo
  // (pricing_table, stats_counter, carousel_slider, before_after_slider,
  // social_proof_bar, voxfunl_audio).
  countdown_timer: CountdownTimer,
  testimonial_card: TestimonialCard,
  alert_banner: AlertBanner,
  comparison_table: ComparisonTable,
  voxfunl_audio: VoxfunlAudio,
  product_assurance: ProductAssurance,

  // H. AUTH BLOCKS
  login_form: LoginForm,

  // H. BLOG BLOCKS
  blog_post_content: BlogPostContent,

  // F. CHECKOUT/CART BLOCKS (5)
  cart_table: CartTable,
  cart_totals: CartTotals,
  coupon_code: CouponCode,
  shipping_estimator: ShippingEstimator,
  checkout_steps: CheckoutSteps,

  // G. ACCOUNT BLOCKS. Previously rendered NULL because no
  // registry entry existed; now backed by real components in commerce/.
  account_navigation: AccountNavigation,
  account_dashboard: AccountDashboard,

  // H. BLOG LANDING BLOCK. blog_widget covers homepage and
  // sidebar usage; blog_grid is the main /blog feed component.
  blog_grid: BlogGrid,
} as unknown) as Record<string, BlockComponent>;

// UNKNOWN BLOCK FALLBACK COMPONENT
export const UnknownBlock: React.FC<{ type: string }> = ({ type }) => {
  return React.createElement('div', { className: 'p-4 border-2 border-dashed border-red-300 bg-red-50 rounded' },
    React.createElement('p', { className: 'text-red-600 font-medium' }, `⚠️ Unknown Block Type: "${type}"`),
    React.createElement('p', { className: 'text-sm text-red-500 mt-1' }, 'This block type is not registered. Please check your block configuration.')
  );
};

// GET BLOCK COMPONENT FUNCTION
/**
 * Get the React component for a given block type
 * @param type - The block type string from Admin (e.g., 'hero_banner')
 * @returns The React component or UnknownBlock fallback
 */
export function getBlockComponent(type: string): BlockComponent {
  if (!type) {
    console.warn('[BlockRegistry] Block type is empty or undefined');
    return createUnknownBlock('(empty)');
  }

  // Try 1: Exact match
  let component = BLOCK_MAP[type];

  // Try 2: Lowercase
  if (!component) {
    component = BLOCK_MAP[type.toLowerCase()];
  }

  // Try 3: Replace hyphens with underscores
  if (!component) {
    component = BLOCK_MAP[type.replace(/-/g, '_')];
  }

  // Try 4: Replace underscores with hyphens
  if (!component) {
    component = BLOCK_MAP[type.replace(/_/g, '-')];
  }

  // Extension seam: only reached when no built-in block matched. Gives a
  // registered extension block a chance before we fall back to "unknown".
  // With zero registrations this is a no-op and behaviour is unchanged.
  if (!component) {
    const ext = getExtensionBlock(type) ?? getExtensionBlock(type.toLowerCase());
    if (ext) return ext;
  }

  if (!component) {
    console.warn(`[BlockRegistry] Unknown block type: "${type}"`);
    return createUnknownBlock(type);
  }

  return component;
}

/**
 * Create an UnknownBlock fallback component
 */
function createUnknownBlock(type: string): BlockComponent {
  // Log once in dev, render nothing in storefront (avoids red debug boxes)
  if (typeof window === 'undefined' || import.meta.env.DEV) {
    console.warn(`[BlockRegistry] Unregistered block type rendered: "${type}"`);
  }
  return () => null;
}

// UTILITY FUNCTIONS
/**
 * Check if a block type is registered
 */
export function isBlockTypeRegistered(type: string): boolean {
  return type in BLOCK_MAP;
}

/**
 * Get all registered block types
 */
export function getRegisteredBlockTypes(): string[] {
  return Object.keys(BLOCK_MAP);
}

/**
 * Get block count
 */
export function getRegisteredBlockCount(): number {
  return Object.keys(BLOCK_MAP).length;
}

// No default export — use named exports only (enables Rollup tree-shaking)
